/**
 * Post-auth landing-route resolution — shared by login, the email-confirm
 * callback, and password-reset / invite-acceptance.
 *
 * Firm-staff roles land on the firm admin console dashboard (/admin/dashboard);
 * the external auditor lands on its read-only surface (/audit); an affiliate
 * finder (role `partner-finder`) lands on its standalone finder dashboard
 * (/partner/finder-dashboard); everyone else (the B2C trustee/beneficiary, role
 * `user`) lands on /app/dashboard.
 *
 * WHY /admin/dashboard and /app/dashboard, NOT bare /admin or /app: BOTH `/admin`
 * (routes/admin/_layout.tsx) and `/app` (routes/app/_layout.tsx) are blank LAYOUT
 * routes with no index route — bare, they render an empty <Outlet />. The actual
 * consoles live at /admin/dashboard and /app/dashboard. The /app layout does
 * self-redirect bare /app → /app/dashboard, but landing the user there DIRECTLY
 * is one less hop and is symmetric with the admin fix (2026-06-22).
 *
 * WHY this module exists: a fresh password login used to hard-navigate EVERY
 * user to `/app` regardless of role, so a firm admin landed on the single-trust
 * B2C dashboard and never saw the firm console (which is fully built, at
 * /admin/dashboard). The role→route mapping lived only in `reset-password.tsx`,
 * so the main login + the email-confirm callback didn't honour it. Centralised
 * here so every entry point routes a firm admin to /admin/dashboard.
 */
import type { UserRole } from './api';

/** Superadmin roles that land on the platform console (/superadmin). Both
 *  spellings appear in the wild: `super_admin` (canonical, set by the
 *  onboarding FA) and `superadmin` (legacy, pre-snake_case migration window).
 *  Mirrors normalizeRole in lib/authz/route-policy.ts, which the /superadmin
 *  guard trusts. Checked BEFORE ADMIN_LANDING_ROLES so a super admin never
 *  lands on the firm console (they would then have to step-up-loop out of it). */
const SUPERADMIN_LANDING_ROLES = new Set<string>(['super_admin', 'superadmin']);

/** Roles that land on the firm admin console. MUST mirror ADMIN_ALLOWED_ROLES
 *  in routes/admin/_layout.tsx — landing somewhere the layout's gate then
 *  bounces is worse than not landing there. `super_admin` is intentionally
 *  ABSENT: it lands on /superadmin (see SUPERADMIN_LANDING_ROLES), NOT the
 *  firm console — routing it here stranded the platform operator on
 *  /admin/client-book and made /superadmin unreachable (fixed 2026-07-03). */
const ADMIN_LANDING_ROLES = new Set<UserRole>(['admin', 'compliance', 'rm', 'director']);

/** Read-only external-auditor surface. `external-auditor` is in zero backend
 *  write sets, so it must NOT land on /app (every write nav there 403s).
 *  Mirrors AUDITOR_ALLOWED_ROLES in routes/audit/_layout.tsx. */
const AUDITOR_LANDING_ROLES = new Set<UserRole>(['external-auditor']);

/** Affiliate finder-partner. `partner-finder` is a canonical backend role
 *  (tenant-roles.ts CANONICAL_ROLES) whose account holds ONLY the standalone
 *  partner surface — landing it on /app/dashboard would strand it on a B2C portal
 *  it has no data for. It is NOT in the UserRole union (the finder surface is a
 *  public, link-reached route), so — like SUPERADMIN_LANDING_ROLES — this is a
 *  Set<string>: the runtime JWT role string still matches. */
const PARTNER_FINDER_LANDING_ROLES = new Set<string>(['partner-finder']);

/** The surface a freshly-authenticated user should land on, by role. Unknown /
 *  absent role → /app/dashboard (the safe B2C default). Firm staff land on
 *  /admin/dashboard. Both target the real dashboard, not the bare blank layout
 *  route (see file header). */
export function landingRouteForRole(
  role: UserRole | undefined,
): '/superadmin' | '/admin/client-book' | '/audit' | '/partner/finder-dashboard' | '/app/dashboard' {
  // Platform operator FIRST — before the firm-console check. A super_admin routed
  // to /admin/client-book lands on a firm console they don't belong in and then
  // has to step-up-loop out of it to reach /superadmin (the P0 this fixes). Handle
  // both spellings; the /superadmin guard trusts both via normalizeRole.
  if (role && SUPERADMIN_LANDING_ROLES.has(role)) return '/superadmin';
  if (role && AUDITOR_LANDING_ROLES.has(role)) return '/audit';
  // Affiliate finder — its own standalone dashboard, never the B2C portal floor.
  if (role && PARTNER_FINDER_LANDING_ROLES.has(role)) return '/partner/finder-dashboard';
  // First-class tenancy: firm staff (incl. a scoped rm) land on the Client Book —
  // their book of clients — not the firm-tenant dashboard.
  if (role && ADMIN_LANDING_ROLES.has(role)) return '/admin/client-book';
  return '/app/dashboard';
}

/** The onboarding surface a signed-in but PRE-ONBOARDING session (no
 *  `app_metadata.tenant_id` yet) should be sent to, by the persona chosen at
 *  signup. It rides on `user_metadata.account_type`: an explicit `'firm'` enters
 *  the firm bootstrap (`/onboarding/firm`); anything else — `'individual'`,
 *  absent, or any legacy value — falls to the B2C doc-first wrapper
 *  (`/onboarding/individual`), so an older account (signed up before the persona
 *  chooser existed) is never stranded on the firm bootstrap.
 *
 *  Single source of truth for a mapping the confirm callback
 *  (routes/auth/callback.tsx), the /app layout guard (routes/app/_layout.tsx),
 *  and the marketing landing safety-net (routes/index.tsx) all need. Accepts the
 *  hydrated Supabase session (the callback and the guards hold the user object;
 *  only the account_type is read, so any session-shaped value works). */
export function preOnboardingRouteForSession(
  session: { user?: { user_metadata?: Record<string, unknown> | null } | null } | null,
): '/onboarding/firm' | '/onboarding/individual' {
  const accountType = session?.user?.user_metadata?.['account_type'];
  return accountType === 'firm' ? '/onboarding/firm' : '/onboarding/individual';
}

/** Decode `{ tenantId, role }` from a Supabase access-token JWT (best-effort;
 *  both undefined on any malformed/absent token). The email-confirm callback
 *  holds only the raw token, not the hydrated user object, so it decodes here.
 *  NOTE: this trusts the token for ROUTING only — every API call is still
 *  verified server-side; a forged claim just changes which screen renders. */
export function landingClaimsFromAccessToken(
  token: string | undefined,
): { tenantId?: string; role?: UserRole } {
  if (!token) return {};
  try {
    const part = token.split('.')[1];
    if (!part) return {};
    const payload = JSON.parse(atob(part)) as {
      app_metadata?: { tenant_id?: string; role?: string };
    };
    return {
      tenantId: payload.app_metadata?.tenant_id,
      role: payload.app_metadata?.role as UserRole | undefined,
    };
  } catch {
    return {};
  }
}
